import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadIcon from "@mui/icons-material/Upload";
import BusinessIcon from "@mui/icons-material/Business";
import { useState, useEffect } from "react";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import FullPageLoader from "../../app/shared/common/FullPageLoader";

/* -------------------- Image Upload Component -------------------- */

interface ImageUploadProps {
  label: string;
  preview: string | null;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete: () => void;
  inputId: string;
}

const ImageUploadBox: React.FC<ImageUploadProps> = ({
  label,
  preview,
  onUpload,
  onDelete,
  inputId,
}) => {
  return (
    <Box
      sx={{
        flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 16px)" },
        mb: { xs: 3, md: 0 },
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        {label}
      </Typography>
      {!preview ? (
        <Box>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={inputId}
            type="file"
            onChange={onUpload}
          />
          <label htmlFor={inputId}>
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{
                py: 1.5,
                borderStyle: "dashed",
                borderWidth: 2,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  borderStyle: "dashed",
                  backgroundColor: alpha("#667eea", 0.05),
                },
              }}
            >
              Upload Image
            </Button>
          </label>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            border: "2px solid",
            borderColor: "divider",
          }}
        >
          <img
            src={preview}
            alt={label}
            style={{
              width: "100%",
              maxHeight: 200,
              objectFit: "contain",
              display: "block",
            }}
          />
          <IconButton
            onClick={onDelete}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const UploadOutletAttachment = observer(() => {
  const { onboardingStore, toasterStore } = useStore();
  const { outletId } = useParams<{ outletId: string }>();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Image states
  const [shopBoardImage, setShopBoardImage] = useState<string | null>(null);
  const [licenseImage, setLicenseImage] = useState<string | null>(null);
  const [panImage, setPanImage] = useState<string | null>(null);
  const [gstImage, setGstImage] = useState<string | null>(null);
  const [fssaiImage, setFssaiImage] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      shopBoardAttachment: null,
      licenseAttachment: null,
      panAttachment: null,
      gstAttachment: null,
      fssaiAttachment: null,
    },
    onSubmit: () => {},
  });

  // Image upload handlers
  const handleImageUpload =
    (
      setter: React.Dispatch<React.SetStateAction<string | null>>,
      fieldName: string,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        formik.setFieldValue(fieldName, file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setter(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

  const handleImageDelete =
    (
      setter: React.Dispatch<React.SetStateAction<string | null>>,
      fieldName: string,
    ) =>
    () => {
      formik.setFieldValue(fieldName, null);
      setter(null);
    };

  const handleUploadAll = async () => {
    if (!outletId) return;

    const formData = new FormData();
    formData.append("id", String(outletId));
    let hasFiles = false;

    if (formik.values.shopBoardAttachment) {
      formData.append("shopBoardAttachment", formik.values.shopBoardAttachment);
      hasFiles = true;
    }
    if (formik.values.licenseAttachment) {
      formData.append("licenseAttachment", formik.values.licenseAttachment);
      hasFiles = true;
    }
    if (formik.values.panAttachment) {
      formData.append("panAttachment", formik.values.panAttachment);
      hasFiles = true;
    }
    if (formik.values.gstAttachment) {
      formData.append("gstAttachment", formik.values.gstAttachment);
      hasFiles = true;
    }
    if (formik.values.fssaiAttachment) {
      formData.append("fssaiAttachment", formik.values.fssaiAttachment);
      hasFiles = true;
    }

    if (hasFiles) {
      setUploading(true);
      try {
        await onboardingStore.uploadAttachments(Number(outletId), formData);
        toasterStore.success("Attachments uploaded successfully");
      } catch (error: any) {
        const msg =
          error?.response?.data?.msg ||
          "Something went wrong. Please try again.";
        toasterStore.error(msg);
      } finally {
        setUploading(false);
      }
    }
    
    // Redirect after completion
    navigate(`/customer/detail/${outletId}`);
  };

  const handleSkip = () => {
    navigate(`/customer/detail/${outletId}`);
  };

  return (
    <Box>
      <FullPageLoader open={uploading} message="Uploading attachments..." />
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          mb: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Upload Outlet Attachments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Outlet created successfully! Please upload documents.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Parent Accordion with Nested Image Upload Sections */}
      <Accordion
        defaultExpanded
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          "&:before": {
            display: "none",
          },
          mb: 3,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderRadius: 3,
            "&:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ImageIcon />
            <Typography variant="h6" fontWeight={600}>
              Outlet Images & Documents
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: { xs: 0, md: 2 } }}>
            {/* Nested Accordion 1: Outlet Images (Shop, License, PAN) */}
            <Accordion
              defaultExpanded
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                "&:before": {
                  display: "none",
                },
                mb: 2,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "&:hover": {
                    backgroundColor: alpha("#667eea", 0.03),
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <ImageIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Outlet Images (Shop, License, PAN)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  <ImageUploadBox
                    label="Shop Board Attachment Image"
                    preview={shopBoardImage}
                    onUpload={handleImageUpload(
                      setShopBoardImage,
                      "shopBoardAttachment",
                    )}
                    onDelete={handleImageDelete(
                      setShopBoardImage,
                      "shopBoardAttachment",
                    )}
                    inputId="shop-board-upload"
                  />
                  <ImageUploadBox
                    label="License Attachment Image"
                    preview={licenseImage}
                    onUpload={handleImageUpload(
                      setLicenseImage,
                      "licenseAttachment",
                    )}
                    onDelete={handleImageDelete(
                      setLicenseImage,
                      "licenseAttachment",
                    )}
                    inputId="license-upload"
                  />
                  <ImageUploadBox
                    label="PAN Attachment Image"
                    preview={panImage}
                    onUpload={handleImageUpload(
                      setPanImage,
                      "panAttachment",
                    )}
                    onDelete={handleImageDelete(
                      setPanImage,
                      "panAttachment",
                    )}
                    inputId="pan-upload"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Nested Accordion 2: GST and FSSAI Attachments */}
            <Accordion
              defaultExpanded
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                "&:before": {
                  display: "none",
                },
                mb: 3,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "&:hover": {
                    backgroundColor: alpha("#667eea", 0.03),
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <DescriptionIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Outlet Images (GST And FSSAI Attachments)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  <ImageUploadBox
                    label="GST Attachment Image"
                    preview={gstImage}
                    onUpload={handleImageUpload(
                      setGstImage,
                      "gstAttachment",
                    )}
                    onDelete={handleImageDelete(
                      setGstImage,
                      "gstAttachment",
                    )}
                    inputId="gst-upload"
                  />
                  <ImageUploadBox
                    label="FSSAI Attachment Image"
                    preview={fssaiImage}
                    onUpload={handleImageUpload(
                      setFssaiImage,
                      "fssaiAttachment",
                    )}
                    onDelete={handleImageDelete(
                      setFssaiImage,
                      "fssaiAttachment",
                    )}
                    inputId="fssai-upload"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Upload Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleSkip}
                sx={{ textTransform: "none", px: 1 }}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadIcon />}
                onClick={handleUploadAll}
                sx={{
                  textTransform: "none",
                  px: 4,
                }}
              >
                Upload & Finish
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
});

export default UploadOutletAttachment;
